// src/firebase/payments.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";
import { generateReferenceCode } from "../utils/referenceCode";
import { withCache, invalidateCachePrefix } from "../utils/cache";

// ── Cache TTLs ────────────────────────────────────────────────────────────────
// Payments are written only by the BOG webhook (server-side, so the in-memory
// cache here won't see those writes immediately). Short TTLs keep the admin
// dashboard fresh; the per-reference-code lookup is safe to hold longer
// because reference codes are immutable.
const TTL_PAYMENTS_LIST = 60 * 1000;       // 1 min — admin recent payments
const TTL_PAYMENTS_REF  = 10 * 60 * 1000;  // 10 min — immutable by ref code

function invalidatePaymentsCache(): void {
  invalidateCachePrefix("payments:");
}

export interface PaymentRecord {
  id?: string;
  referenceCode: string;
  coachId: string;
  coachEmail?: string;
  coachName?: string;
  competitionId: string;
  competitionName: string;
  totalAmount: number;
  athleteCount: number;
  athletes: Array<{
    athleteId: string;
    firstName: string;
    lastName: string;
    club: string;
    division: string;
    weightCategory: string;
    category: string;
    price: number;
  }>;
  status: "pending" | "confirmed" | "failed";
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  bogTransactionId?: string;
}

/**
 * Creates a mock payment record (no real money transfer).
 * Returns the reference code.
 */
export const createPayment = async (data: {
  coachId: string;
  coachEmail?: string;
  competitionId: string;
  competitionName: string;
  totalAmount: number;
  athleteCount: number;
  athletes: Array<{
    athleteId: string;
    firstName: string;
    lastName: string;
    club: string;
    division: string;
    weightCategory: string;
    category: string;
    price: number;
  }>;
}): Promise<string> => {
  const referenceCode = await generateReferenceCode();

  const paymentData = {
    referenceCode,
    coachId: data.coachId,
    coachEmail: data.coachEmail,
    competitionId: data.competitionId,
    competitionName: data.competitionName,
    totalAmount: data.totalAmount,
    athleteCount: data.athleteCount,
    athletes: data.athletes,
    status: "pending" as const,
    createdAt: Timestamp.now(),
  };

  await addDoc(collection(db, "payments"), paymentData);
  invalidatePaymentsCache();
  return referenceCode;
};

/**
 * Fetches the most recent payments (for admin dashboard).
 * @param maxResults - Number of payments to return (default 10)
 */
export const getRecentPayments = async (maxResults = 10): Promise<PaymentRecord[]> =>
  withCache(`payments:recent:${maxResults}`, TTL_PAYMENTS_LIST, async () => {
    const q = query(
      collection(db, "payments"),
      orderBy("createdAt", "desc"),
      limit(maxResults)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PaymentRecord[];
  });

/**
 * Fetches all payments made by a specific coach.
 */
export const getPaymentsByCoach = async (coachId: string): Promise<PaymentRecord[]> =>
  withCache(`payments:coach:${coachId}`, TTL_PAYMENTS_LIST, async () => {
    const q = query(
      collection(db, "payments"),
      where("coachId", "==", coachId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PaymentRecord[];
  });

/**
 * Fetches a payment record by its reference code.
 * Returns null if not found.
 * Reference codes are immutable, so the cache TTL is longer here.
 */
export const getPaymentByReferenceCode = async (referenceCode: string): Promise<PaymentRecord | null> =>
  withCache(`payments:ref:${referenceCode}`, TTL_PAYMENTS_REF, async () => {
    const q = query(collection(db, "payments"), where("referenceCode", "==", referenceCode));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as PaymentRecord;
  });

/**
 * Updates payment status (e.g., after successful BOG webhook – future use).
 */
export const updatePaymentStatus = async (paymentId: string, status: PaymentRecord["status"]) => {
  const ref = doc(db, "payments", paymentId);
  await updateDoc(ref, { status, updatedAt: Timestamp.now() });
  invalidatePaymentsCache();
};
